import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { togglePricingPlanUpgrade } from '../../../../redux/main/main.actions';
import { selectDiagramsList } from '../../../../redux/main/main.selectors';
import { selectAuthUser } from '../../../../redux/auth/auth.selectors';
import {
    selectWorkspacePlan,
    selectWorkspaceRestrictions,
    selectWorkspaceMembers,
    selectCurrentWorkspace,
} from '../../../../redux/workspace/workspace.selectors';
import { getPlan } from '../../../../redux/workspace/workspace.plans';

// ─── Icons ────────────────────────────────────────────────────

const LayoutIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
    </svg>
);

const TableIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="12" y1="3" x2="12" y2="21" />
    </svg>
);

const BoxIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
    </svg>
);

const StickyIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5L15.5 3Z" /><polyline points="14 3 14 8 21 8" />
    </svg>
);

const UsersIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const FolderIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
);

const TrendUpIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd"
            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);

const ZapIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);

const ShieldCheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11.5 14.5 16 9" />
    </svg>
);

const CrownIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 4l3 12h14l3-12-5 4-5-4-5 4-1 0z" /><line x1="3" y1="20" x2="21" y2="20" />
    </svg>
);

// ─── Section wrapper ──────────────────────────────────────────

const UsageSection = ({ icon, title, badge, children, className = '' }) => (
    <div className={`usg-section ${className}`}>
        <div className='usg-section-header'>
            <div className='usg-section-header-left'>
                {icon && <span className='usg-section-icon'>{icon}</span>}
                <span className='usg-section-title'>{title}</span>
            </div>
            {/* {badge && <span className='usg-section-badge'>{badge}</span>} */}
        </div>
        <div className='usg-section-body'>
            {children}
        </div>
    </div>
);

// ─── Limit bar ────────────────────────────────────────────────

const LimitBar = ({ icon, label, used, limit, unit = '' }) => {
    const unlimited = limit === -1 || limit === null;
    const pct = unlimited ? 0 : Math.min((used / limit) * 100, 100);
    const warning = !unlimited && pct >= 80;
    const critical = !unlimited && pct >= 95;

    return (
        <div className={`usg-limit-row ${critical ? 'critical' : warning ? 'warning' : ''}`}>
            <div className='usg-limit-top'>
                <div className='usg-limit-left'>
                    {icon && <span className='usg-limit-icon'>{icon}</span>}
                    <span className='usg-limit-label'>{label}</span>
                </div>
                <span className='usg-limit-count'>
                    <span className='usg-limit-used'>{used}{unit && ` ${unit}`}</span>
                    <span className='usg-limit-separator'>/</span>
                    <span className='usg-limit-max'>{unlimited ? '∞' : `${limit}${unit ? ` ${unit}` : ''}`}</span>
                </span>
            </div>
            <div className='usg-bar'>
                <div
                    className={`usg-bar-fill ${critical ? 'critical' : warning ? 'warning' : ''} ${unlimited ? 'unlimited' : ''}`}
                    style={{ width: unlimited ? '8%' : `${pct}%` }}
                />
            </div>
        </div>
    );
};

// ─── Plan features per tier ──────────────────────────────────

const PLAN_FEATURES = {
    free: [
        'Up to 3 diagrams',
        '1 seat',
        'Basic export',
    ],
    basic: [
        'Up to 20 diagrams',
        // '5 seats',
        'Team collaboration',
        'Version history',
        'Priority support',
    ],
    professional: [
        'Unlimited diagrams',
        // 'Unlimited seats',
        'Unlimited teams',
        'Advanced sharing',
        'Priority support',
    ],
};

// ─── Onboarding steps ─────────────────────────────────────────

const ONBOARDING_STEPS = [
    { id: 'tables', title: 'Add new tables to the diagram', link: '/' },
    { id: 'diagram', title: 'Create a new diagram', link: '/' },
    { id: 'invite', title: 'Invite members to the workspace', link: '/settings/workspace/' },
];

// ─── Main component ──────────────────────────────────────────

const SettingsUsage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux data
    const currentUser = useSelector(selectAuthUser);
    const diagrams = useSelector(selectDiagramsList) || [];
    const plan = useSelector(selectWorkspacePlan) || 'free';
    const restrictions = useSelector(selectWorkspaceRestrictions) || getPlan(plan);
    const members = useSelector(selectWorkspaceMembers) || [];
    const workspace = useSelector(selectCurrentWorkspace);

    // Computed counts
    const diagramCount = diagrams.length;
    const memberCount = members.length;

    // Count tables, areas, notes across all diagrams
    const { tableCount, areaCount, noteCount } = useMemo(() => {
        let tables = 0, areas = 0, notes = 0;
        diagrams.forEach(d => {
            tables += d.tables_count || d.tablesCount || 0;
            areas += d.areas_count || d.areasCount || 0;
            notes += d.notes_count || d.notesCount || 0;
        });
        return { tableCount: tables, areaCount: areas, noteCount: notes };
    }, [diagrams]);

    const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
    const isPaid = plan !== 'free';

    // Renewal date from workspace
    const renewsAt = workspace?.renews_at || workspace?.renewsAt;
    const renewalText = isPaid && renewsAt
        ? `Renews ${new Date(renewsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : isPaid ? 'Active subscription' : 'No subscription needed';

    // Onboarding completion
    const onboardingStatus = useMemo(() => {
        return ONBOARDING_STEPS.map(step => {
            let isDone = false;
            if (step.id === 'tables') isDone = tableCount > 0;
            if (step.id === 'diagram') isDone = diagramCount > 0;
            if (step.id === 'invite') isDone = memberCount > 1;
            return { ...step, isDone };
        });
    }, [tableCount, diagramCount, memberCount]);

    const onboardingDone = onboardingStatus.filter(s => s.isDone).length;
    const onboardingTotal = onboardingStatus.length;
    const onboardingPct = Math.round((onboardingDone / onboardingTotal) * 100);

    // Stats data
    const stats = [
        { icon: <LayoutIcon />, label: t('settings_page.usage.label_diagrams'), value: diagramCount, max: restrictions.caps.diagrams },
        { icon: <TableIcon />, label: t('settings_page.usage.label_tables'), value: tableCount, max: null },
        { icon: <BoxIcon />, label: t('settings_page.usage.label_areas'), value: areaCount, max: null },
        { icon: <StickyIcon />, label: t('settings_page.usage.label_notes'), value: noteCount, max: null },
        { icon: <UsersIcon />, label: t('settings_page.usage.label_members'), value: memberCount, max: restrictions.caps.members },
    ];

    // Limits data
    const limits = [
        { icon: <LayoutIcon />, label: t('settings_page.usage.label_diagrams'), used: diagramCount, limit: restrictions.caps.diagrams },
        { icon: <TableIcon />, label: t('settings_page.usage.label_tables_per_diagram'), used: tableCount, limit: restrictions.caps.tables },
        { icon: <BoxIcon />, label: t('settings_page.usage.label_areas_per_diagram'), used: areaCount, limit: restrictions.caps.areas },
        { icon: <StickyIcon />, label: t('settings_page.usage.label_notes_per_diagram'), used: noteCount, limit: restrictions.caps.notes },
    ];

    const planFeatures = PLAN_FEATURES[plan] || PLAN_FEATURES.free;

    return (
        <div className='usg-wrap'>

            {/* ── Plan Hero ────────────────────────────────────────── */}
            {/* <div className='usg-plan-hero'>
                <div className='usg-plan-hero-bg' />
                <div className='usg-plan-hero-content'>
                    <div className='usg-plan-hero-left'>
                        <span className='usg-plan-hero-label'>Current Plan</span>
                        <div className='usg-plan-hero-name-row'>
                            <span className='usg-plan-hero-name'>{planLabel}</span>
                            <span className='usg-plan-hero-active-badge'>Active</span>
                        </div>
                        <span className='usg-plan-hero-renews'>{renewalText}</span>
                    </div>
                    {plan !== 'professional' && (
                        <button
                            className='usg-upgrade-btn'
                            onClick={() => dispatch(togglePricingPlanUpgrade('settings'))}
                        >
                            Upgrade plan
                        </button>
                    )}
                </div>
                <div className='usg-plan-features'>
                    {planFeatures.map((feat) => (
                        <div className='usg-plan-feature' key={feat}>
                            <span className='usg-plan-feature-check'><CheckCircleIcon /></span>
                            <span className='usg-plan-feature-text'>{feat}</span>
                        </div>
                    ))}
                </div>
            </div> */}

            {/* ── Quick Stats ──────────────────────────────────────── */}
            <UsageSection
            // icon={<TrendUpIcon />}
            title={t('settings_page.usage.overview_title')}
            >
                <div className='usg-stats-grid'>
                    {stats.map(s => {
                        const unlimited = s.max === -1 || s.max === null;
                        const pct = unlimited ? 0 : s.max > 0 ? Math.min((s.value / s.max) * 100, 100) : 0;
                        return (
                            <div className='usg-stat-card' key={s.label}>
                                <div className='usg-stat-card-top'>
                                    <div className='usg-stat-icon'>{s.icon}</div>
                                    {!unlimited && s.max > 0 && (
                                        <span className='usg-stat-cap'>
                                            {s.value}<span className='usg-stat-cap-sep'>/</span>{s.max}
                                        </span>
                                    )}
                                </div>
                                <div className='usg-stat-card-bottom'>
                                    <span className='usg-stat-value'>{s.value}</span>
                                    <span className='usg-stat-label'>{s.label}</span>
                                </div>
                                {!unlimited && s.max > 0 && (
                                    <div className='usg-stat-bar'>
                                        <div
                                            className={`usg-stat-bar-fill ${pct >= 95 ? 'critical' : pct >= 80 ? 'warning' : ''}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </UsageSection>

            {/* ── Resource Limits ───────────────────────────────────── */}
            <UsageSection
            // icon={<ZapIcon />}
            title={t('settings_page.usage.resource_limits_title')}
            // badge={`${planLabel} plan`}
            >
                <div className='usg-limits-list'>
                    {limits.map(l => <LimitBar key={l.label} {...l} />)}
                </div>
            </UsageSection>

            {/* ── Plan Comparison ───────────────────────────────────── */}
            {plan !== 'professional' && (
                <div className='usg-compare-card'>
                    <div className='usg-compare-left'>
                        <div className='usg-compare-title'>{t('settings_page.usage.need_more_title')}</div>
                        <div className='usg-compare-desc'>
                            {plan === 'free'
                                ? t('settings_page.usage.need_more_free')
                                : t('settings_page.usage.need_more_paid')}
                        </div>
                    </div>
                    <button
                        className='usg-upgrade-btn'
                        onClick={() => dispatch(togglePricingPlanUpgrade('settings'))}
                    >
                        {t('b.upgrade')}
                    </button>
                </div>
            )}

            {/* ── Getting Started ───────────────────────────────────── */}
            {/* <UsageSection title='Getting Started' badge={`${onboardingDone}/${onboardingTotal}`}>
                <div className='usg-onboarding-progress'>
                    <div className='usg-onboarding-bar'>
                        <div className='usg-onboarding-bar-fill' style={{ width: `${onboardingPct}%` }} />
                    </div>
                    <span className='usg-onboarding-pct'>{onboardingPct}% complete</span>
                </div>

                <div className='usg-onboarding-list'>
                    {onboardingStatus.map((step, i) => (
                        <div
                            key={step.id}
                            className={`usg-onboarding-item ${step.isDone ? 'done' : ''}`}
                        >
                            <div className='usg-onboarding-item-left'>
                                <span className={`usg-onboarding-check ${step.isDone ? 'done' : ''}`}>
                                    {step.isDone
                                        ? <CheckCircleIcon />
                                        : <span className='usg-onboarding-dot' />
                                    }
                                </span>
                                <span className='usg-onboarding-title'>{step.title}</span>
                            </div>
                            <span className='usg-onboarding-action'>
                                {step.isDone ? 'Done' : 'Start'}
                            </span>
                        </div>
                    ))}
                </div>
            </UsageSection> */}

        </div>
    );
};

export default SettingsUsage;
